import React from 'react'
import { Paper, PaperContent } from '@e3dc-react/shell/Elements/Paper'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Grid } from '@mui/material'
import { GraphicBaseCard, useGraphicBaseCard } from 'src/Libs/graph-cards'
import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { UnitTypes } from 'src/Libs/types'
import { UNIT_FACTOR_WATT_TO_WATTHOUR } from 'src/Configuration/defaults'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import SystemSettingsBlankView from 'src/Libs/system-settings/component/SystemSettingsBlankView'
import { WallboxType } from 'src/Libs/wallboxes/api'
import { getWallboxTotalPowerMax } from 'src/Libs/wallboxes/utils'

const DEFAULT_VALUE_FALLBACK = 0

interface Props {
  model?: WallboxConfigurationModel
  powerLevelL1: number[]
  powerLevelL2: number[]
  powerLevelL3: number[]
  /** Signed sum of all phases (W), undefined while no live data is available. Used for EDSN only. */
  totalPower?: number
}

/*
 * Renders wallbox overview
 */
const WallboxOverview: React.FC<Props> = ({ model, powerLevelL3, powerLevelL2, powerLevelL1, totalPower }) => {
  const { t } = useCustomTranslation()

  return (
    <Paper title={t('systemSettings.powerMeter.overview.sectionTitle')}>
      <PaperContent>
        <WallboxOverviewContent
          model={model}
          powerLevelL1={powerLevelL1}
          powerLevelL2={powerLevelL2}
          powerLevelL3={powerLevelL3}
          totalPower={totalPower}
        />
      </PaperContent>
    </Paper>
  )
}

export default WallboxOverview

/*
 * Picks the overview variant: a single total power gauge for EDSN, phase gauges for all other wallboxes
 */
const WallboxOverviewContent: React.FC<Props> = ({ model, powerLevelL1, powerLevelL2, powerLevelL3, totalPower }) => {
  if (!model) return <SystemSettingsBlankView />

  if (model.wallboxType === WallboxType.EDSN) return <WallboxTotalPowerGraph model={model} totalPower={totalPower} />

  return <WallboxOverviewGraphs model={model} powerLevelL1={powerLevelL1} powerLevelL2={powerLevelL2} powerLevelL3={powerLevelL3} />
}

/*
 * Renders wallbox overview graphs
 */
const WallboxOverviewGraphs: React.FC<{
  model: WallboxConfigurationModel
  powerLevelL1: number[]
  powerLevelL2: number[]
  powerLevelL3: number[]
}> = ({ model, powerLevelL1, powerLevelL3, powerLevelL2 }) => {
  const { t } = useCustomTranslation()
  const { valueOnlyTagLabel } = useGraphicBaseCard()

  const sumEnergy = [
    model?.pmEnergyL1 || DEFAULT_VALUE_FALLBACK,
    model?.pmEnergyL2 || DEFAULT_VALUE_FALLBACK,
    model?.pmEnergyL3 || DEFAULT_VALUE_FALLBACK,
  ]
  const powerMeters = [powerLevelL1, powerLevelL2, powerLevelL3]

  return (
    <Grid container direction="row" justifyContent="space-between" spacing={3} sx={{ pt: 3, pb: 3 }}>
      {powerMeters.map((boundaries, index) => {
        const summary = (sumEnergy[index] || DEFAULT_VALUE_FALLBACK) / UNIT_FACTOR_WATT_TO_WATTHOUR

        return (
          <Grid key={index} item xs={12} sm={4} md={4}>
            <GraphicBaseCard
              tagLabel={valueOnlyTagLabel}
              key={index}
              values={boundaries}
              min={model.minPowerPerPhase}
              max={model.maxPowerPerPhase}
              title={t(`systemSettings.wallbox.overview.powerL${index + 1}`)}
              unit={UnitTypes.watt}
              subtitle={`${getLocalizedNumber(summary, 2)} ${UnitTypes.kiloWattHour}`}
            />
          </Grid>
        )
      })}
    </Grid>
  )
}

/*
 * Renders a single gauge with the overall power of all phases.
 * Interim for EDSN until firmware provides DC power: the pointer shows the absolute value, the tag keeps the sign (negative = discharging).
 */
const WallboxTotalPowerGraph: React.FC<{
  model: WallboxConfigurationModel
  totalPower?: number
}> = ({ model, totalPower }) => {
  const { t } = useCustomTranslation()
  const { valueOnlyTagLabel } = useGraphicBaseCard()
  const values = totalPower === undefined ? [] : [totalPower]

  return (
    <Grid container direction="row" justifyContent="center" spacing={3} sx={{ pt: 3, pb: 3 }}>
      <Grid item xs={12} sm={4} md={4}>
        <GraphicBaseCard
          tagLabel={valueOnlyTagLabel}
          values={values}
          pointerValues={values.map(Math.abs)}
          min={0}
          // without a valid limit GraphicBaseCard renders the tag only, no pointer
          max={getWallboxTotalPowerMax(model) ?? 0}
          title={t('systemSettings.wallbox.overview.power')}
          unit={UnitTypes.watt}
        />
      </Grid>
    </Grid>
  )
}
